import { useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import SubjectIcon from "@mui/icons-material/Subject";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "../../utils/convertToJalali";
import jalaliday from "jalaliday";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import jalaali from "jalaali-js";
import { ListLetterHead, ListEmza } from "../../services/letter";

dayjs.extend(jalaliday);

export function AddLetterForm({ formData, onChange }) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width:960px)");

  const handleChange = (field) => (event) => {
    onChange(field, event.target.value);
  };

  const handleEditConfig = () => {
    navigate("/adjustment", {
      state: {
        formData,
        templateFile: formData.file || null,
      },
    });
  };

  const handleCheckboxChange = (field) => (event) => {
    onChange(field, event.target.checked);
  };

  // Helper function to load IDs
  const loadIds = async (type) => {
    try {
      if (type === "sarbarg") {
        const sarbargResponse = await ListLetterHead();
        const sarbargList = sarbargResponse.data || [];
        if (sarbargList.length > 0) {
          const firstSarbarg = sarbargList[0];
          const sarbargIdStr =
            firstSarbarg.idsarbargstr || firstSarbarg.id || "";
          const sarbargId = firstSarbarg.idsarbarg || firstSarbarg.id || 0;
          if (sarbargIdStr) {
            onChange("idsarbargstr", sarbargIdStr);
          }
          if (sarbargId) {
            onChange("idsarbarg", sarbargId);
          }
        }
      } else if (type === "emza") {
        const emzaResponse = await ListEmza();
        const emzaList = emzaResponse.data || [];
        if (emzaList.length > 0) {
          const firstEmza = emzaList[0];
          const emzaIdStr = firstEmza.idemzastr || firstEmza.id || "";
          const emzaId = firstEmza.idemza || firstEmza.id || 0;
          if (emzaIdStr) {
            onChange("idemzastr", emzaIdStr);
          }
          if (emzaId) {
            onChange("idemza", emzaId);
          }
        }
      }
    } catch (error) {
      console.error(`Error loading ${type} IDs:`, error);
    }
  };

  const handleRooneveshtChange = (event) => {
    const value = event.target.value;
    onChange("roonevesht", value);
    // Set isroonevesht to true if there's any text, false if empty
    onChange("isroonevesht", value.trim().length > 0);
  };

  // Load sarbarg and emza IDs on mount
  useEffect(() => {
    const loadIds = async () => {
      try {
        // Only set IDs if they're not already set (to preserve values from adjustment page)
        if (!formData.idsarbargstr) {
          const sarbargResponse = await ListLetterHead();
          const sarbargList = sarbargResponse.data || [];
          if (sarbargList.length > 0) {
            const firstSarbarg = sarbargList[0];
            const sarbargIdStr =
              firstSarbarg.idsarbargstr || firstSarbarg.id || "";
            const sarbargId = firstSarbarg.idsarbarg || firstSarbarg.id || 0;
            if (sarbargIdStr) {
              onChange("idsarbargstr", sarbargIdStr);
            }
            if (sarbargId) {
              onChange("idsarbarg", sarbargId);
            }
          }
        }

        if (!formData.idemzastr) {
          const emzaResponse = await ListEmza();
          const emzaList = emzaResponse.data || [];
          if (emzaList.length > 0) {
            const firstEmza = emzaList[0];
            const emzaIdStr = firstEmza.idemzastr || firstEmza.id || "";
            const emzaId = firstEmza.idemza || firstEmza.id || 0;
            if (emzaIdStr) {
              onChange("idemzastr", emzaIdStr);
            }
            if (emzaId) {
              onChange("idemza", emzaId);
            }
          }
        }
      } catch (error) {
        console.error("Error loading sarbarg/emza IDs:", error);
        // Silently fail - don't show error to user
      }
    };

    loadIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        py: { xs: 2, md: 3 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "900px",
          p: { xs: 2, sm: 3, md: 4 },
          bgcolor: "background.paper",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        {/* Edit Config Button - Desktop Only */}
        {isDesktop && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: -2,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SettingsIcon />}
              onClick={handleEditConfig}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 1,
                fontWeight: 500,
                borderWidth: 1.5,
                "&:hover": {
                  borderWidth: 1.5,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                },
              }}
            >
              ویرایش تنظیمات
            </Button>
          </Box>
        )}

        {/* Use Template and Signature Checkboxes */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <SettingsIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.938rem", sm: "1rem" },
                }}
              >
                تنظیمات نامه
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 3 },
                flexWrap: "wrap",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      formData.useSignature !== undefined
                        ? formData.useSignature
                        : true
                    }
                    onChange={(e) => {
                      handleCheckboxChange("useSignature")(e);
                      // If unchecked, set idemzastr to "0"
                      if (!e.target.checked) {
                        onChange("idemzastr", "0");
                      } else {
                        // If checked, restore the IDs
                        loadIds("emza");
                      }
                    }}
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 24,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      userSelect: "none",
                      fontSize: "0.875rem",
                    }}
                  >
                 امضا درج شود
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      formData.useTemplate !== undefined
                        ? formData.useTemplate
                        : true
                    }
                    onChange={(e) => {
                      handleCheckboxChange("useTemplate")(e);
                      // If unchecked, set idsarbargstr to "0"
                      if (!e.target.checked) {
                        onChange("idsarbargstr", "0");
                      } else {
                        // If checked, restore the IDs
                        loadIds("sarbarg");
                      }
                    }}
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 24,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      userSelect: "none",
                      fontSize: "0.875rem",
                    }}
                  >
                    سربرگ اعمال شود
                  </Typography>
                }
              />
            </Box>
          </CardContent>
        </Card>

        {/* Header Section - Date, Number, Attachment */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <CalendarTodayIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.938rem", sm: "1rem" },
                }}
              >
                اطلاعات نامه
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 3 },
                flexWrap: "wrap",
              }}
            >
              {/* Date Picker Section */}
              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 200px" }, minWidth: 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                  }}
                >
                  تاریخ:
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="fa"
                >
                  <DatePicker
                    format="YYYY/MM/DD"
                    value={
                      formData.dateletter
                        ? dayjs(formData.dateletter).calendar("jalali")
                        : null
                    }
                    onChange={(newValue) => {
                      if (newValue && dayjs.isDayjs(newValue)) {
                        try {
                          const jalaliDate = newValue.calendar("jalali");
                          const jalaliStr = jalaliDate.format("YYYY/MM/DD");
                          const [jalaliYear, jalaliMonth, jalaliDay] = jalaliStr
                            .split("/")
                            .map(Number);
                          const gregorian = jalaali.toGregorian(
                            jalaliYear,
                            jalaliMonth,
                            jalaliDay
                          );
                          const date = new Date(
                            Date.UTC(
                              gregorian.gy,
                              gregorian.gm - 1,
                              gregorian.gd,
                              0,
                              0,
                              0,
                              0
                            )
                          );
                          const isoString = date.toISOString();
                          onChange("dateletter", isoString);
                        } catch (error) {
                          console.error("Error converting Jalali date:", error);
                          const isoString = newValue.toISOString();
                          onChange("dateletter", isoString);
                        }
                      } else {
                        onChange("dateletter", "");
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        variant: "outlined",
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "white",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* Number Section */}
              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 150px" }, minWidth: 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                  }}
                >
                  شماره:
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formData.numletter || ""}
                  onChange={handleChange("numletter")}
                  placeholder="---"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                    },
                  }}
                />
              </Box>

              {/* Attachment Section */}
              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 150px" }, minWidth: 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                  }}
                >
                  پیوست:
                </Typography>
                <Box
                  sx={{
                    bgcolor: "white",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",

                    display: "flex",
                    alignItems: "center",
                    minHeight: 40,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isAttachment || false}
                        onChange={handleCheckboxChange("isAttachment")}
                        size="small"
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: 20,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          color: "text.primary",
                        }}
                      >
                        {formData.isAttachment ? "دارد" : "ندارد"}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Sender & Receiver */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.938rem", sm: "1rem" },
                }}
              >
                فرستنده و گیرنده
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                  }}
                >
                  از:
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.sender || ""}
                  onChange={handleChange("sender")}
                  placeholder="نام فرستنده"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: "0.813rem",
                  }}
                >
                  به:
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.receiver || ""}
                  onChange={handleChange("receiver")}
                  placeholder="نام گیرنده"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                    },
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Title */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <SubjectIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.938rem", sm: "1rem" },
                }}
              >
                موضوع نامه
              </Typography>
            </Box>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              value={formData.title || ""}
              onChange={handleChange("title")}
              placeholder="عنوان نامه را وارد کنید"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Body Text */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.938rem", sm: "1rem" },
                }}
              >
                متن نامه
              </Typography>
            </Box>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              rows={6}
              value={formData.textletter || ""}
              onChange={handleChange("textletter")}
              placeholder="متن نامه ..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  "& textarea": {
                    lineHeight: 2,
                    textAlign: "justify",
                    fontSize: "0.938rem",
                  },
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Post Script (Roonevesht) */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <EditNoteIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.938rem", sm: "1rem" },
                }}
              >
                رونوشت (اختیاری)
              </Typography>
            </Box>
            <TextField
              multiline
              fullWidth
              variant="outlined"
              rows={3}
              value={formData.roonevesht || ""}
              onChange={handleRooneveshtChange}
              placeholder={`رونوشت${" "}(اختیاری)`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  "& textarea": {
                    lineHeight: 2,
                    textAlign: "justify",
                    fontSize: "0.938rem",
                  },
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card
          elevation={0}
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: { xs: "100%", sm: "300px" },
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formData.fullname || ""}
                  onChange={handleChange("fullname")}
                  placeholder=" نام و نام خانوادگی امضا کننده"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      "& input": {
                        textAlign: "center",
                      },
                    },
                  }}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={formData.postName || ""}
                  onChange={handleChange("postName")}
                  placeholder=" سمت امضا کننده"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      "& input": {
                        textAlign: "center",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Box>
  );
}
